import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
  LinearProgress
} from '@mui/material';
import {
  GroupAdd as GroupAddIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Share as ShareIcon,
  Star as StarIcon,
  ThumbUp as ThumbUpIcon,
  Visibility as VisibilityIcon,
  ArrowForward as ArrowForwardIcon,
  EmojiPeople as EmojiPeopleIcon,
  ConnectWithoutContact as ConnectIcon,
  Handshake as HandshakeIcon
} from '@mui/icons-material';

// Empty State Component for 0 connections
const EmptyNetworkState = ({ onFindConnections, onImportContacts }) => {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: <StarIcon color="primary" />,
      title: "Get Quality Reviews",
      description: "Connected users can review your business with verified trust scores"
    },
    {
      icon: <ThumbUpIcon color="primary" />,
      title: "Receive Referrals", 
      description: "Your network can refer clients and customers to grow your business"
    },
    {
      icon: <TrendingUpIcon color="primary" />,
      title: "Build Trust",
      description: "Connected relationships increase your platform trust score"
    },
    {
      icon: <VisibilityIcon color="primary" />,
      title: "Increase Visibility",
      description: "More connections means more profile views and opportunities"
    }
  ];

  const quickActions = [
    {
      icon: <SearchIcon />,
      title: "Find People You Know",
      description: "Search for colleagues, clients, and business partners",
      action: () => onFindConnections(),
      variant: "contained"
    },
    {
      icon: <BusinessIcon />,
      title: "Browse Businesses",
      description: "Connect with businesses you've worked with",
      action: () => navigate('/business-search'),
      variant: "outlined"
    },
    {
      icon: <ShareIcon />,
      title: "Share Your Profile",
      description: "Invite people to connect with you",
      action: () => navigate('/profile/share'),
      variant: "outlined"
    }
  ];

  return (
    <Box>
      {/* Main Empty State Card */}
      <Paper variant='outlined' sx={{ p: 4, mb: 3, border: 'none', textAlign: 'center' }}>
        <Box sx={{ mb: 3 }}>
          <ConnectIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
            Start Building Your Network
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto' }}>
            Your network is your net worth! Connect with trusted colleagues, clients, and business partners 
            to unlock reviews, referrals, and growth opportunities.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<GroupAddIcon />}
            onClick={onFindConnections}
            sx={{ minWidth: 200 }}
          >
            Find Connections
          </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={<ShareIcon />}
            onClick={() => navigate('/profile/share')}
          >
            Share Profile
          </Button>
        </Box>
      </Paper>

      {/* Benefits Grid */}
      <Paper variant='outlined' sx={{ p: 3, mb: 3, border: 'none' }}>
        <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
          Why Build Your Network?
        </Typography>
        <Grid container spacing={2}>
          {benefits.map((benefit, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Card variant="outlined" sx={{ p: 2, height: '100%', border: 'none', bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box sx={{ flexShrink: 0 }}>
                    {benefit.icon}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      {benefit.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {benefit.description}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Quick Actions */}
      <Paper variant='outlined' sx={{ p: 3, border: 'none' }}>
        <Typography variant="h6" gutterBottom>
          Get Started Today
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {quickActions.map((action, index) => (
            <Card 
              key={index}
              variant="outlined" 
              sx={{ 
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { 
                  bgcolor: 'grey.50',
                  transform: 'translateY(-1px)',
                  boxShadow: 1
                }
              }}
              onClick={action.action}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  {action.icon}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1">
                    {action.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {action.description}
                  </Typography>
                </Box>
                <ArrowForwardIcon color="action" />
              </CardContent>
            </Card>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

// Growing Network State for 1-2 connections
const GrowingNetworkState = ({ connections, onFindConnections, networkStats }) => {
  const navigate = useNavigate();
  
  const progress = Math.min((connections.length / 10) * 100, 100); // Progress towards 10 connections
  
  const nextSteps = [
    {
      icon: <SearchIcon />,
      title: "Find More Connections",
      description: `Add ${10 - connections.length} more to reach 10 connections`,
      action: () => onFindConnections()
    },
    {
      icon: <HandshakeIcon />,
      title: "Engage With Your Network",
      description: "Review businesses your connections have worked with",
      action: () => navigate('/activity-feed')
    },
    {
      icon: <ShareIcon />,
      title: "Get Referrals",
      description: "Ask your connections to refer your business",
      action: () => navigate('/referrals')
    }
  ];

  return (
    <Box>
      {/* Progress Card */}
      <Paper variant='outlined' sx={{ p: 3, mb: 3, border: 'none' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <EmojiPeopleIcon color="primary" />
          <Typography variant="h6">
            Growing Your Network
          </Typography>
          <Chip 
            label={`${connections.length}/10`} 
            color="primary" 
            variant="outlined" 
            size="small" 
          />
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Great start! You have {connections.length} connection{connections.length !== 1 ? 's' : ''}. 
          Build a network of 10+ trusted connections to maximize your opportunities.
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Network Progress</Typography>
            <Typography variant="body2" color="primary">
              {Math.round(progress)}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ height: 8, borderRadius: 1 }}
          />
        </Box>

        <Button
          variant="contained"
          startIcon={<GroupAddIcon />}
          onClick={onFindConnections}
          fullWidth
        >
          Add More Connections
        </Button>
      </Paper>

      {/* Current Connections */}
      <Paper variant='outlined' sx={{ p: 3, mb: 3, border: 'none' }}>
        <Typography variant="h6" gutterBottom>
          Your Connections ({connections.length})
        </Typography>
        {/* This will be replaced by the actual ConnectionCard components */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Your connections will appear here. Stay engaged with them to build stronger relationships.
        </Typography>
      </Paper>

      {/* Network Impact Stats */}
      {networkStats && (
        <Paper variant='outlined' sx={{ p: 3, mb: 3, border: 'none' }}>
          <Typography variant="h6" gutterBottom>
            Your Network Impact
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {networkStats.networkReviews || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Network Reviews
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {networkStats.networkReferrals || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Network Referrals
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Next Steps */}
      <Paper variant='outlined' sx={{ p: 3, border: 'none' }}>
        <Typography variant="h6" gutterBottom>
          Grow Your Network
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {nextSteps.map((step, index) => (
            <Card 
              key={index}
              variant="outlined" 
              sx={{ 
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { 
                  bgcolor: 'grey.50',
                  transform: 'translateY(-1px)',
                  boxShadow: 1
                }
              }}
              onClick={step.action}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                  {step.icon}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2">
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </Box>
                <ArrowForwardIcon color="action" />
              </CardContent>
            </Card>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

// Main component to determine which state to show
const NetworkMiddleColumn = ({ 
  connections = [], 
  onFindConnections,
  networkStats,
  ConnectionCard // Pass the ConnectionCard component as a prop
}) => {
  const navigate = useNavigate();

  // Determine which state to show based on connection count
  if (connections.length === 0) {
    return (
      <EmptyNetworkState 
        onFindConnections={onFindConnections}
        onImportContacts={() => console.log('Import contacts')}
      />
    );
  } else if (connections.length <= 2) {
    return (
      <Box>
        <GrowingNetworkState 
          connections={connections}
          onFindConnections={onFindConnections}
          networkStats={networkStats}
        />
        {/* Show the actual connections using the provided ConnectionCard component */}
        {ConnectionCard && connections.map((connection) => (
          <ConnectionCard key={connection.userId} connection={connection} />
        ))}
      </Box>
    );
  } else {
    // For 3+ connections, show the normal connection list
    return (
      <Box>
        {ConnectionCard && connections.map((connection) => (
          <ConnectionCard key={connection.userId} connection={connection} />
        ))}
      </Box>
    );
  }
};

export default NetworkMiddleColumn;